import React, { useState } from "react"
import Layout from "../components/layout"
import SEO from "../components/seo"
import { Link } from "gatsby"
import Button, { ButtonVariants } from "../components/button"
import { formatCurrency } from "../utils/format"
import { products } from "../data/products"
import { testimonials } from "../data/testimonials"
import { AnimateSharedLayout, motion, AnimatePresence } from "framer-motion"
import AddIcon from "../assets/icons/add.svg"
import { faqs } from "../data/faq"
import { useCloudinaryImage } from "../hooks/useCloudinaryImage"
import { useSiteMetadata } from "../hooks/useSiteMetadata"
import Slider from "react-slick"
import "slick-carousel/slick/slick.css"
import "slick-carousel/slick/slick-theme.css"
import resolveConfig from "tailwindcss/resolveConfig"
import tailwindConfig from "../../tailwind.config"
import ForwardIcon from "../assets/icons/forward.svg"
import BackIcon from "../assets/icons/back.svg"
import Modal from "../components/modal"
import { ContactForm } from "../components/contactForm"

const resolvedTailwindConfig = resolveConfig(tailwindConfig)
const removeUnits = x => parseInt(x.replace(/(px|em|rem)$/, ""))

function ProductCard({ id, image, name, price }) {
  const imageUrl = useCloudinaryImage(image)
  return (
    <div className="flex h-full flex-col md:mr-4">
      <motion.div
        layout
        layoutId={`product-${id}-image`}
        className="relative aspect-ratio-product-thumbnail mb-6"
      >
        <img
          src={imageUrl}
          className="absolute rounded top-0 left-0 w-full h-full object-cover"
          alt={name}
        />
      </motion.div>
      <div className="mb-6 flex flex-1 flex-col">
        <motion.h3
          layout
          layoutId={`product-${id}-name`}
          className="font-body font-bold text-heading-18 lg:text-heading-20 text-gray-900 mb-2 flex-1"
        >
          {name}
        </motion.h3>
        <p className="text-body-14 lg:text-body-18 text-gray-900">
          <span className="sr-only">Price:</span>
          {formatCurrency(price)}
        </p>
      </div>
      <Link
        to={`/product/${id}`}
        className="uppercase text-body-14 lg:text-body-18 font-bold text-primary focus:underline focus:outline-none"
      >
        View Product
      </Link>
    </div>
  )
}

function TestimonialCard({ image, name, testimonial }) {
  return (
    <motion.div
      layout
      whileHover={{ scale: 1.05 }}
      className="border rounded flex flex-col items-center text-center px-8 py-9 bg-white hover:z-10"
    >
      <img src={image} className="bg-gray-200 mb-6 avatar" alt={name} />
      <p className="text-gray-700 text-body-18 mb-3">{name}</p>
      <p className="text-base text-gray-700">{testimonial}</p>
    </motion.div>
  )
}

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false)

  const toggleOpen = () => setOpen(open => !open)

  const handleKeyUp = e => {
    if (e.code === "Enter" || e.keyCode === 13) {
      e.preventDefault()
      toggleOpen()
    }
  }

  return (
    <motion.li layout>
      <motion.div
        tabIndex={0}
        onKeyUp={handleKeyUp}
        onClick={toggleOpen}
        className="flex items-center justify-between pb-6 pt-9 text-black text-heading-18 lg:text-heading-20 border-b select-none cursor-pointer uppercase"
        layout
      >
        <span className="mr-4">{question}</span>
        <AddIcon className="w-8 h-6" />
      </motion.div>
      <AnimatePresence>
        {open && (
          <motion.p
            className="text-gray-900 text-base lg:text-body-18 pt-4"
            layout
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {answer}
          </motion.p>
        )}
      </AnimatePresence>
    </motion.li>
  )
}

function FAQs() {
  return (
    <motion.ul layout className="list-none">
      {faqs.map(faq => (
        <FAQItem key={faq.id} {...faq} />
      ))}
    </motion.ul>
  )
}

function NextArrow({ className, style, onClick }) {
  return (
    <button
      className={className}
      style={{ ...style }}
      onClick={onClick}
      aria-label="Next product"
    >
      <ForwardIcon />
    </button>
  )
}

function PrevArrow({ className, style, onClick }) {
  return (
    <button
      className={className}
      style={{ ...style }}
      onClick={onClick}
      aria-label="Previous product"
    >
      <BackIcon />
    </button>
  )
}

function ProductCarousel() {
  const props = {
    infinite: false,
    swipeToScroll: true,
    slidesToShow: 4,
    slidesToScroll: 1,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    responsive: [
      {
        breakpoint: removeUnits(resolvedTailwindConfig.theme.screens.lg),
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: removeUnits(resolvedTailwindConfig.theme.screens.sm),
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  }
  return (
    <Slider {...props}>
      {products.map(product => (
        <ProductCard key={product.id} {...product} />
      ))}
    </Slider>
  )
}

function AboutStemCellsModal() {
  const [open, setOpen] = useState(false)
  const videoUrl = useCloudinaryImage("about-stem-cells")

  return (
    <React.Fragment>
      <Button onClick={() => setOpen(true)}>Learn More</Button>
      <Modal open={open} onRequestClose={() => setOpen(false)}>
        <div className="relative aspect-ratio-grid-pane-image">
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video
            className="absolute top-0 left-0 w-full h-full"
            src={videoUrl}
            autoPlay={true}
            controls={true}
          />
        </div>
      </Modal>
    </React.Fragment>
  )
}

// eslint-disable-next-line no-unused-vars
function TestimonialsSection() {
  return (
    <section className="py-20 w-full bg-white">
      <div className="container content-container container-centered">
        <div className="flex flex-col items-center text-center mb-9">
          <h2 className="text-heading-36 lg:text-heading-48 text-black">
            Testimonials
          </h2>
        </div>
        <AnimateSharedLayout>
          <motion.div
            layout
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
          >
            {testimonials.map(testimonial => (
              <TestimonialCard key={testimonial.id} {...testimonial} />
            ))}
          </motion.div>
        </AnimateSharedLayout>
      </div>
    </section>
  )
}

function HeroSlider() {
  const images = useCloudinaryImage([
    "hero-slider/slide-1",
    "hero-slider/slide-2",
    "hero-slider/slide-3",
    "hero",
  ])
  const props = {
    infinite: true,
    autoplay: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    className: "w-full h-full",
    arrows: false,
  }
  return (
    <Slider {...props}>
      {images.map(image => (
        <img
          key={image}
          src={image}
          className="w-full h-full object-cover"
          alt=""
        />
      ))}
    </Slider>
  )
}

export default function Home() {
  const metadata = useSiteMetadata()
  const [videoUrl] = useCloudinaryImage(["about-stem-cells"])
  const [distributorMode, setDistributorMode] = useState(false)

  return (
    <Layout>
      <SEO title="Home" />
      <section id="home" className="w-full h-screen max-h-hero relative">
        <HeroSlider />
        <div className="w-full h-full overlay flex mb-6 absolute top-0 left-0">
          <div className="container container-centered flex flex-col items-start justify-center h-full">
            <motion.h1
              initial={{ y: 30, opacity: 0 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.75 }}
              className="text-white text-heading-48 lg:text-heading-96 font-normal mb-1"
            >
              Health is Wealth
            </motion.h1>
            <motion.p
              initial={{ y: 30, opacity: 0 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="text-white text-body-20 lg:text-body-24 w-full mb-8"
              style={{
                maxWidth: "35rem",
              }}
            >
              Take absolute care of your body, browse through our selection of
              innovative stem cell products that can revitalise and rejuvanate
              you.
            </motion.p>
            <motion.span
              initial={{ y: 30, opacity: 0 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="flex flex-col md:flex-row"
            >
              <Button
                className="mb-6 md:mr-6 md:mb-0"
                variant={ButtonVariants.CallToAction}
                to="/#products"
              >
                View Our Products
              </Button>
              <Button to="/distributors">Become a distributor</Button>
            </motion.span>
          </div>
        </div>
      </section>
      <section className="py-20 w-full bg-white">
        <div className="container content-container container-centered grid grid-cols-1 grid-rows-2 lg:grid-cols-2 lg:grid-rows-1 grid-flow-col gap-4 sm:gap-8 lg:gap-40">
          <div className="order-2 lg:order-1">
            <h2 className="capitalize mb-8 lg:mb-8 text-heading-36 lg:text-heading-48 text-black">
              Stem cells is the future
            </h2>
            <p className="text-gray-800 text-body-18 lg:text-body-20 text-justify mb-18">
              A breakthrough health and youth preservation system, based on
              highly innovative and award winning plant stem cell science
              -Phytocelltec Malus Domestica, clinic proven to rejuvenate and
              revitalize epidermal stem cells and tissues regeneration - for a
              healthier, stronger and younger looking you.
            </p>
            <AboutStemCellsModal />
          </div>
          <div className="order-1 lg:order-2">
            <div className="relative w-full bg-gray-200 aspect-ratio-grid-pane-image">
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <video
                className="absolute top-0 left-0 w-full h-full"
                src={videoUrl}
                controls={true}
              />
            </div>
          </div>
        </div>
      </section>
      <section id="intro" className="py-20 w-full bg-section">
        <div className="container content-container container-centered">
          <div className="flex flex-col items-center text-center mb-20">
            <h2 className="text-heading-36 lg:text-heading-48 text-black mb-4">
              Do more with SuperLife
            </h2>
            <p
              className="text-black text-body-18 lg:text-body-20"
              style={{
                maxWidth: "30.0625rem",
              }}
            >
              Bringing you better physical health with no damage to your
              financial health
            </p>
          </div>
          <div className="grid gap-12 md:gap-8 lg:gap-12 md:grid-cols-3">
            <div className="flex flex-col items-center">
              <div aria-hidden={true} className="inline-block flex-1">
                <img
                  src={require("../assets/images/heart.jpg")}
                  className="rounded-full w-48 h-48 object-cover text-primary mb-12"
                  alt=""
                />
              </div>
              <p className="text-body-18 text-center">
                Your body matters.
                <br />
                Take care of it in the best way possible.
              </p>
            </div>
            <Link
              to="/#contact"
              onClick={() => setDistributorMode(true)}
              className="flex flex-col items-center"
            >
              <div aria-hidden={true} className="inline-block flex-1">
                <img
                  src={require("../assets/images/distributor.jpg")}
                  className="rounded-full w-48 h-48 text-primary mb-12"
                  alt=""
                />
              </div>
              <p className="text-body-18 text-center">
                Want to impact lives in your local area?
                <br />
                Become a{" "}
                <Link
                  to="/distributors"
                  className="text-primary focus:underline"
                >
                  distributor
                </Link>
              </p>
            </Link>
            <div className="flex flex-col items-center">
              <div aria-hidden={true} className="inline-block flex-1">
                <img
                  src={require("../assets/images/delivery.jpg")}
                  className="rounded-full w-48 h-48 text-primary mb-12"
                  alt=""
                />
              </div>
              <p className="text-body-18 text-center">
                We offer <b className="uppercase">free</b> fast world wide
                delivery.
                <br />
                Location is not a barrier to improving your health.
              </p>
            </div>
          </div>
        </div>
      </section>
      <section id="products" className="py-20 w-full bg-white">
        <div className="container content-container container-centered">
          <div className="flex flex-col items-center text-center mb-9">
            <h2 className="text-heading-36 lg:text-heading-48 text-black">
              Our Products
            </h2>
            <p
              className="text-black text-body-18 lg:text-body-20"
              style={{
                maxWidth: "30.0625rem",
              }}
            >
              Browse through our selection of healthy products
            </p>
          </div>
          <ProductCarousel />
        </div>
      </section>
      <section id="faq" className="py-20 w-full bg-section">
        <AnimateSharedLayout>
          <motion.div
            layout
            className="container content-container container-centered grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-16"
          >
            <motion.h2
              layout
              className="text-heading-36 lg:text-heading-48 text-black self-center"
              style={{
                maxWidth: "35rem",
              }}
            >
              Frequently Asked Questions
            </motion.h2>
            <FAQs />
          </motion.div>
        </AnimateSharedLayout>
      </section>
      <section id="contact" className="pt-32 pb-24 w-full bg-white">
        <div className="container content-container container-centered">
          <div className="flex flex-col items-center text-center mb-9">
            <h2 className="text-heading-36 lg:text-heading-48 text-black">
              Contact Us
            </h2>
          </div>
          <div className="container container-centered grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-24">
            <div>
              <div className="text-body-18 lg:text-body-20 text-gray-700 mb-8">
                <p>Have any questions?</p>
                <p>Interested in joining our network of distributors?</p>
                <p>Reach out to us!</p>
              </div>
              <div className="text-body-18 lg:text-body-20 text-gray-700">
                <p className="text-gray-800">{metadata.title}</p>
                <p>{metadata.contactDetails.address}</p>
                <p>
                  <a
                    className="hover:underline focus:underline"
                    href={`tel:${metadata.contactDetails.phoneNumber}`}
                  >
                    {metadata.contactDetails.phoneNumber}
                  </a>
                </p>
                <p>
                  <a
                    className="hover:underline focus:underline"
                    href={`mailto:${metadata.contactDetails.email}`}
                  >
                    {metadata.contactDetails.email}
                  </a>
                </p>
              </div>
            </div>
            <ContactForm distributorMode={distributorMode} />
          </div>
        </div>
      </section>
    </Layout>
  )
}
